import React, { ChangeEvent, createContext, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { TFunction } from 'i18next';
import { useStockCountCreate } from '../hooks/useStockCountCreate';
import { User, Item, PrimaryCategoryStockCount, StockCheckItem, CountZoneDto, StockCheckLocation, ItemModel, StockCheckTypesResponse, ApproverRole, CountZoneTypeResponse } from '../../../app/services/api/generated';
import { Blocker } from 'react-router-dom';
import { ApproverType } from '../../../enums/ApproverType';
import { StockAdjustmentTiming } from '../../../enums/StockAdjustmentTiming';
export interface StockCountCreateValues {
  t: TFunction<'createStockCount', undefined>,
  isCategoriesModalOpen: boolean,
  isProductTypeModalOpen: boolean,
  isItemsModalOpen: boolean,
  isBulkModalOpen: boolean,
  hasCategories: boolean,
  isCategoryDeleteModalOpen: boolean,
  isProductTypeDeleteModalOpen: boolean,
  isDeleteModalOpen: boolean,
  isEndOfMonthCountModalOpen: boolean,
  existingStockCheckId: number | null,
  existingStockCheckStatus: number | null,
  categoryId: number | undefined,
  productType: string,
  itemNumber: number | undefined,
  hasItems: boolean,
  isMobile: boolean,
  allUsers: User[],
  handleSmartCountChange: (event: SelectChangeEvent) => void,
  handleQtyChange: (id: number, newValue: string) => void,
  handleValueChange: (id: number, newValue: string) => void,
  handleLinkClick: () => void,
  handleDateChange: (event: ChangeEvent<HTMLInputElement>) => void,
  handleUserSelect: (event: SelectChangeEvent<string>) => void,
  handleSelectChange: (event: SelectChangeEvent<number>) => void,
  generateTimeOptions: () => { value: number; label: string; }[],
  handleInputValue: (event: ChangeEvent<HTMLInputElement>) => void,
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void,
  submitStock: () => void,
  handleClickBulkClick: () => void,
  handleClick: () => void,
  deleteItem: () => void,
  deleteCategory: () => void,
  deleteProductType: () => void,
  handleClickOpen: () => void,
  handleProductTypeClickOpen: () => void,
  handleClose: () => void,
  handleBulkItems: (data: Item[]) => void,
  handleItems: (data: Item[], ticked: boolean) => void,
  deleteItemRow: (id: number) => void,
  deleteCategoryRow: (id: number) => void,
  deleteProductTypeRow: (productType: string) => void,
  deleteItems: (data: Item[]) => void,
  deleteCategories: (data: PrimaryCategoryStockCount[]) => void,
  deleteProductTypes: (data: string[]) => void,
  handleCategories: (data: PrimaryCategoryStockCount[]) => void,
  handleProductTypes: (data: string[]) => void,
  handleSliderChange: (event: Event, newValue: number | number[]) => void,
  handleIsTimeLockedChanged: (event: SelectChangeEvent) => void,
  setIsEndOfMonthCountModalOpen: (isOpen: boolean) => void,
  name: string,
  selectedDate: string | undefined,
  today: string | undefined,
  selectedUser: string,
  approverType: ApproverType,
  approverRoles: ApproverRole[],
  selectedApproverRoleId: number | undefined,
  handleApproverTypeChange: (event: SelectChangeEvent<number>) => void,
  handleApproverRoleSelect: (event: SelectChangeEvent<number>) => void,
  approvalValue: number | undefined,
  selectedTime: number | undefined,
  isTimeLocked: boolean,
  sliderValue: number,
  categoryData: PrimaryCategoryStockCount[],
  productTypeData: string[],
  convertedItems: StockCheckItem[],
  itemsData: Item[],
  closeSnackBar: () => void,
  isSnackBarOpen: boolean,
  snackBarSeverity: 'error' | 'warning' | 'info' | 'success',
  snackbarMessage: string,
  snackBarDuration: number | null,
  isSmartCount: boolean,
  blocker: Blocker,
  handleLeavePageModalClose: () => void,
  handleLeavePageModalConfirm: () => void,
  isSubmitting: boolean,
  currencySymbol: string,
  isCountZoneConfigEnabled: boolean,
  countZonesData: CountZoneDto[],
  handleAddCountZonesClick: () => void,
  isCountZonesModalOpen: boolean,
  isCountZonesDeleteModalOpen: boolean,
  handleCountZones: (data: CountZoneDto[]) => void,
  convertedCountZones: StockCheckLocation[],
  deleteCountZoneRow: (id: number) => void,
  deleteCountZone: () => void,
  deleteCountZones: (data: CountZoneDto[]) => void,
  countZoneId: number | undefined,
  handleViewCountZoneItemsClick: (countZoneId: number) => void,
  isCountZonesWithItemsModalOpen: boolean,
  countZoneItemsData: ItemModel[],
  hasCountZones: boolean,
  isCountZoneTypesModalOpen: boolean,
  isCountZoneTypesDeleteModalOpen: boolean,
  countZoneTypesData: CountZoneTypeResponse[],
  countZoneTypeId: number | undefined,
  hasCountZoneTypes: boolean,
  handleAddCountZoneTypesClick: () => void,
  handleCountZoneTypes: (data: CountZoneTypeResponse[]) => void,
  deleteCountZoneTypeRow: (id: number) => void,
  deleteCountZoneType: () => void,
  deleteCountZoneTypes: (data: CountZoneTypeResponse[]) => void,
  handleStockCheckTypeChange: (event: SelectChangeEvent<number>) => void,
  stockCheckTypes: StockCheckTypesResponse[],
  selectedStockCheckType: StockCheckTypesResponse | null,
  strictEmailApprovalEnabled: boolean | null,
  autoApprovalEnabled: boolean,
  isToleranceSliderToggled: boolean,
  handleIsToleranceSliderToggledChange: (event: SelectChangeEvent) => void,
  adjustmentTiming: StockAdjustmentTiming,
  handleAdjustmentTimingChange: (event: SelectChangeEvent<number>) => void
}

export const StockCountCreateContext = createContext<null | StockCountCreateValues>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const StockCountCreateProvider = ({ children }: ProviderProps) => {
  const {
    t,
    isCategoriesModalOpen,
    isProductTypeModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    hasCategories,
    isCategoryDeleteModalOpen,
    isProductTypeDeleteModalOpen,
    isDeleteModalOpen,
    isEndOfMonthCountModalOpen,
    existingStockCheckId,
    existingStockCheckStatus,
    categoryId,
    productType,
    itemNumber,
    hasItems,
    isMobile,
    allUsers,
    handleSmartCountChange,
    handleQtyChange,
    handleValueChange,
    handleLinkClick,
    handleDateChange,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleClickBulkClick,
    handleClick,
    deleteItem,
    deleteCategory,
    deleteProductType,
    handleClickOpen,
    handleProductTypeClickOpen,
    handleClose,
    handleBulkItems,
    handleItems,
    deleteItemRow,
    deleteCategoryRow,
    deleteProductTypeRow,
    deleteItems,
    deleteCategories,
    deleteProductTypes,
    handleCategories,
    handleProductTypes,
    handleSliderChange,
    handleIsTimeLockedChanged,
    setIsEndOfMonthCountModalOpen,
    name,
    selectedDate,
    today,
    selectedUser,
    approverType,
    approverRoles,
    selectedApproverRoleId,
    handleApproverTypeChange,
    handleApproverRoleSelect,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    productTypeData,
    convertedItems,
    itemsData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    isSmartCount,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    isSubmitting,
    currencySymbol,
    isCountZoneConfigEnabled,
    handleAddCountZonesClick,
    isCountZonesModalOpen,
    isCountZonesDeleteModalOpen,
    handleCountZones,
    countZonesData,
    convertedCountZones,
    deleteCountZoneRow,
    deleteCountZone,
    deleteCountZones,
    countZoneId,
    handleViewCountZoneItemsClick,
    isCountZonesWithItemsModalOpen,
    countZoneItemsData,
    hasCountZones,
    isCountZoneTypesModalOpen,
    isCountZoneTypesDeleteModalOpen,
    countZoneTypesData,
    countZoneTypeId,
    hasCountZoneTypes,
    handleAddCountZoneTypesClick,
    handleCountZoneTypes,
    deleteCountZoneTypeRow,
    deleteCountZoneType,
    deleteCountZoneTypes,
    handleStockCheckTypeChange,
    stockCheckTypes,
    selectedStockCheckType,
    strictEmailApprovalEnabled,
    autoApprovalEnabled,
    isToleranceSliderToggled,
    handleIsToleranceSliderToggledChange,
    adjustmentTiming,
    handleAdjustmentTimingChange,
  } = useStockCountCreate();

  const passedValue = {
    t,
    isCategoriesModalOpen,
    isProductTypeModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    hasCategories,
    isCategoryDeleteModalOpen,
    isProductTypeDeleteModalOpen,
    isDeleteModalOpen,
    isEndOfMonthCountModalOpen,
    existingStockCheckId,
    existingStockCheckStatus,
    categoryId,
    productType,
    itemNumber,
    hasItems,
    isMobile,
    allUsers,
    handleSmartCountChange,
    handleQtyChange,
    handleValueChange,
    handleLinkClick,
    handleDateChange,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleClickBulkClick,
    handleClick,
    deleteItem,
    deleteCategory,
    deleteProductType,
    handleClickOpen,
    handleProductTypeClickOpen,
    handleClose,
    handleBulkItems,
    handleItems,
    deleteItemRow,
    deleteCategoryRow,
    deleteProductTypeRow,
    deleteItems,
    deleteCategories,
    deleteProductTypes,
    handleCategories,
    handleProductTypes,
    handleSliderChange,
    handleIsTimeLockedChanged,
    setIsEndOfMonthCountModalOpen,
    name,
    selectedDate,
    today,
    selectedUser,
    approverType,
    approverRoles,
    selectedApproverRoleId,
    handleApproverTypeChange,
    handleApproverRoleSelect,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    productTypeData,
    convertedItems,
    itemsData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    isSmartCount,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    isSubmitting,
    currencySymbol,
    isCountZoneConfigEnabled,
    handleAddCountZonesClick,
    isCountZonesModalOpen,
    isCountZonesDeleteModalOpen,
    handleCountZones,
    countZonesData,
    convertedCountZones,
    deleteCountZoneRow,
    deleteCountZone,
    deleteCountZones,
    countZoneId,
    handleViewCountZoneItemsClick,
    isCountZonesWithItemsModalOpen,
    countZoneItemsData,
    hasCountZones,
    isCountZoneTypesModalOpen,
    isCountZoneTypesDeleteModalOpen,
    countZoneTypesData,
    countZoneTypeId,
    hasCountZoneTypes,
    handleAddCountZoneTypesClick,
    handleCountZoneTypes,
    deleteCountZoneTypeRow,
    deleteCountZoneType,
    deleteCountZoneTypes,
    handleStockCheckTypeChange,
    stockCheckTypes,
    selectedStockCheckType,
    strictEmailApprovalEnabled,
    autoApprovalEnabled,
    isToleranceSliderToggled,
    handleIsToleranceSliderToggledChange,
    adjustmentTiming,
    handleAdjustmentTimingChange,
  };

  const fireBaseProviderValue = useMemo(() => ({ passedValue }), [passedValue]);

  return (
    <StockCountCreateContext.Provider value={fireBaseProviderValue.passedValue}>
      {children}
    </StockCountCreateContext.Provider>
  );
};